import type { SuccessResponseBody } from '#core/types/api'
import { useMutation, useQuery } from '@pinia/colada'
import { defineStore } from 'pinia'

export const useNotificationsStore = defineStore('notifications', () => {
  const toast = useAppToast()

  const notifications = ref<SuccessResponseBody<'/notifications'>['items']>([])
  const lastRequestedAt = ref<SuccessResponseBody<'/notifications'>['requestedAt'] | undefined>(undefined)
  let timeoutId: NodeJS.Timeout | null = null

  const notificationsQuery = useQuery({
    ...buildApiQuery('/notifications', {
      params: () => ({
        query: { createdAt: lastRequestedAt.value },
      }),
    }),
    enabled: false,
  })

  async function fetch() {
    await notificationsQuery.refetch()

    if (notificationsQuery.data.value) {
      setNotifications([...notifications.value, ...notificationsQuery.data.value.items])
      lastRequestedAt.value = notificationsQuery.data.value.requestedAt
    }

    // Schedule the next request after the current one completes
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(fetch, 60_000)
  }

  function startPolling() {
    fetch()
  }

  function stopPolling() {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }

  function setNotifications(data: SuccessResponseBody<'/notifications'>['items']) {
    notifications.value = data
  }

  const { mutateAsync: markAsReadMutate } = useMutation(
    buildApiMutation('/notifications/mark-as-read', { method: 'put', options: { toast: true } }),
  )

  const { mutateAsync: deleteNotificationMutate } = useMutation(
    buildApiMutation('/notifications/delete', { method: 'delete', options: { toast: true } }),
  )

  const { mutateAsync: deleteAllNotificationsMutate } = useMutation(
    buildApiMutation('/notifications/delete-all', { method: 'delete', options: { toast: true } }),
  )

  async function markAllAsRead() {
    if (!lastRequestedAt.value) {
      return
    }

    try {
      await markAsReadMutate({
        body: {
          untilDate: lastRequestedAt.value,
        },
      })
      notifications.value.forEach((n) => {
        n.read = true
      })
    }
    catch {
      toast.error('Failed to mark all notifications as read. Please try again.')
    }
  }

  async function deleteNotification(id: string) {
    try {
      await deleteNotificationMutate({
        body: {
          id,
        },
      })
      notifications.value = notifications.value.filter(n => n.id !== id)
    }
    catch {
      toast.error('Failed to delete notification. Please try again.')
    }
  }

  async function deleteAllNotifications() {
    if (!lastRequestedAt.value) {
      return
    }

    try {
      await deleteAllNotificationsMutate({
        body: {
          untilDate: lastRequestedAt.value,
        },
      })
      notifications.value = []
    }
    catch {
      toast.error('Failed to delete all notifications. Please try again.')
    }
  }

  return {
    notifications,
    lastRequestedAt,
    deleteAllNotifications,
    deleteNotification,
    startPolling,
    stopPolling,
    setNotifications,
    markAllAsRead,
  }
})
